import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';

import {
  calculateTotalCarbs,
  calculateTotalFat,
  calculateTotalFiber,
  calculateTotalProtein,
} from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a memoized array of formatted nutrition data based on the provided ingredients.
 *
 * The hook calculates the total carbohydrates, protein, fat, and calories
 * for the given ingredients and formats these values.
 *
 * Each entry in the returned array includes a label (e.g., "Carbohydrates", "Protein") and the
 * corresponding formatted value (e.g., "30G").
 *
 * The hook omits entries for any nutritional metric that is not valid.
 */
export const useProductDynamicMicroNutritionData = (params: Params): NutritionData => {
  const { ingredients, calories, useFallbackValues } = params;

  const { formatMessage } = useIntl();

  // ─── Computed Data ───────────────────────────────────────────────────

  const fallbackValue = useFallbackValues ? 0 : null;

  const totalCarbs = calculateTotalCarbs(ingredients) ?? fallbackValue;
  const totalProtein = calculateTotalProtein(ingredients) ?? fallbackValue;
  const totalFat = calculateTotalFat(ingredients) ?? fallbackValue;
  const totalFiber = calculateTotalFiber(ingredients) ?? fallbackValue;

  const hasValidCarbs = typeof totalCarbs === 'number';
  const hasValidProtein = typeof totalProtein === 'number';
  const hasValidFat = typeof totalFat === 'number';
  const hasValidFiber = typeof totalFiber === 'number';
  const hasValidCalories = typeof calories === 'number';

  // ─── Messages ────────────────────────────────────────────────────────

  const carbsLabel = formatMessage(messages.carbs);
  const proteinLabel = formatMessage(messages.protein);
  const fatLabel = formatMessage(messages.fat);
  const fiberLabel = formatMessage(messages.fiber);
  const caloriesLabel = formatMessage(messages.calories);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const formatGramValue = useCallback(
    (value: number) => formatMessage(messages.gramValue, { value }),
    [formatMessage],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useMemo<NutritionData>(() => {
    const carbsEntry = hasValidCarbs
      ? [{ label: carbsLabel, value: formatGramValue(totalCarbs) }]
      : [];

    const proteinEntry = hasValidProtein
      ? [{ label: proteinLabel, value: formatGramValue(totalProtein) }] // prettier-ignore
      : [];

    const fatEntry = hasValidFat ? [{ label: fatLabel, value: formatGramValue(totalFat) }] : [];

    const fiberEntry = hasValidFiber
      ? [{ label: fiberLabel, value: formatGramValue(totalFiber) }]
      : [];

    const caloriesEntry = hasValidCalories ? [{ label: caloriesLabel, value: calories }] : [];

    return [...proteinEntry, ...carbsEntry, ...fatEntry, ...fiberEntry, ...caloriesEntry];
  }, [
    formatGramValue,
    hasValidCarbs,
    carbsLabel,
    totalCarbs,
    hasValidProtein,
    proteinLabel,
    totalProtein,
    hasValidFat,
    fatLabel,
    totalFat,
    hasValidFiber,
    fiberLabel,
    totalFiber,
    hasValidCalories,
    caloriesLabel,
    calories,
  ]);
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly carbs: NoMessageValues;
  readonly protein: NoMessageValues;
  readonly fat: NoMessageValues;
  readonly calories: NoMessageValues;
  readonly fiber: NoMessageValues;
  readonly gramValue: { readonly value: MessageValue };
}>({
  carbs: {
    defaultMessage: 'Carbs',
    description: 'Menu | Nutrition | Carbs | Label',
  },
  protein: {
    defaultMessage: 'Protein',
    description: 'Menu | Nutrition | Protein | Label',
  },
  fat: {
    defaultMessage: 'Fat',
    description: 'Menu | Nutrition | Fat | Label',
  },
  calories: {
    defaultMessage: 'Cal',
    description: 'Menu | Nutrition | Calories | Label',
  },
  fiber: {
    defaultMessage: 'Fiber',
    description: 'Menu | Nutrition | Fiber | Label',
  },
  gramValue: {
    defaultMessage: '{value}g',
    description: 'Menu | Nutrition | Gram value',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  ingredients: Ingredient[] | readonly Ingredient[];
  calories: number | null;
  useFallbackValues?: boolean;
};

type NutritionData = { label: string; value: string | number }[];

type Ingredient = {
  totalFatG: number | null;
  totalCarbsG: number | null;
  proteinG: number | null;
  dietaryFiberG: number | null;
  quantity?: number;
};
